import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { PaymentCreditForm } from "../PaymentCreditForm/PaymentCreditForm";
import { usePaymentCreditMutations } from "../api";
import { emptyPaymentCreditForm } from "../types";
import type { PaymentCreditFormValues } from "../types";

const FORM_ID = "payment-credit-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = usePaymentCreditMutations();
  const [submitting, setSubmitting] = useState(false);
  // Remounting the form via key resets it to empty after "Save and Add Another".
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  // The native Save button submits directly (nextAction stays "edit"); "Save and
  // Add Another" sets "new" then programmatically submits. handleSubmit resets it
  // to "edit" so a later plain Save navigates as expected.
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: PaymentCreditFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({
          variant: "success",
          text: `'${values.paymentSource}' saved`,
        });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: {
              variant: "success",
              text: `'${values.paymentSource}' saved`,
            },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create payment credit", err);
      setToast({ variant: "danger", text: "Could not save payment credit." });
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  return (
    <>
      <AdminFormPage
        title="Add a Payment Credit"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <PaymentCreditForm
          key={formKey}
          id={FORM_ID}
          initialValues={emptyPaymentCreditForm}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
